import { useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { Home, ShoppingCart, Store as StoreIcon, User, LogOut } from "lucide-react";

const tabs = [
  { to: "/app", label: "Today", icon: Home, end: true },
  { to: "/app/stores", label: "Stores", icon: StoreIcon },
  { to: "/app/orders", label: "Orders", icon: ShoppingCart },
  { to: "/app/profile", label: "Me", icon: User },
];

function useLocationPing() {
  useEffect(() => {
    if (!navigator.geolocation) return;
    let cancelled = false;
    const ping = () => {
      if (cancelled) return;
      navigator.geolocation.getCurrentPosition(
        (p) => {
          if (cancelled) return;
          api
            .post("/locations", {
              lat: p.coords.latitude,
              lng: p.coords.longitude,
              accuracy: p.coords.accuracy || null,
            })
            .catch(() => {});
        },
        () => {},
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
      );
    };
    ping();
    const iv = setInterval(ping, 90 * 1000); // every 90s
    const onVisible = () => {
      if (document.visibilityState === "visible") ping();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
}

export default function SalesmanLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  useLocationPing();
  const signOut = () => {
    logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)] flex flex-col" data-testid="salesman-layout">
      <header className="sticky top-0 z-30 bg-[color:var(--bg-base)]/90 backdrop-blur border-b border-[color:var(--border)]">
        <div className="flex items-center justify-between px-4 h-14 max-w-xl mx-auto">
          <Link to="/app" className="flex items-center gap-2">
            <img src="/kridam-logo.png" alt="GM Kridam Foods" className="h-9 w-auto" />
            <span className="font-display font-bold">GM Kridam Foods</span>
          </Link>
          <button onClick={signOut} className="text-[color:var(--muted)]" data-testid="salesman-logout">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="flex-1 pb-24 max-w-xl w-full mx-auto">
        <div className="px-4 py-4">
          <Outlet />
        </div>
      </main>

      <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-[color:var(--border)] z-40" data-testid="salesman-bottom-nav">
        <div className="max-w-xl mx-auto grid grid-cols-4">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-3 text-xs ${
                  isActive ? "text-[color:var(--primary)]" : "text-[color:var(--muted)]"
                }`
              }
              data-testid={`salesman-tab-${t.label.toLowerCase()}`}
            >
              <t.icon className="h-5 w-5" strokeWidth={2.25} />
              <span className="font-medium">{t.label}</span>
            </NavLink>
          ))}
        </div>
        <div className="h-[env(safe-area-inset-bottom)]" />
      </nav>
    </div>
  );
}
